
import React, { useState, useEffect } from 'react';
import { getUserById } from '../../../api/userApi';
import { toast } from 'react-toastify';
import { X, User, Edit2, MapPin, Phone, Mail, Calendar, Badge, CreditCard } from 'lucide-react';

const ViewUser = ({ userId, isOpen, onClose }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      try {
        setIsLoading(true);
        const userData = await getUserById(userId);
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user details:', error);
        toast.error('Failed to load user details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && userId) {
      fetchUserDetails();
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  // Loading skeleton for user details
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="flex flex-col items-center mb-6">
        <div className="h-40 w-40 rounded-full bg-gray-200"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '';
    return `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    return status === 1
      ? 'bg-green-100 text-green-800 border border-green-200'
      : 'bg-yellow-100 text-yellow-800 border border-yellow-200';
  };

  // Get user type badge class and label
  const getUserTypeBadge = (type) => {
    if (type === 'admin') {
      return {
        class: 'bg-purple-100 text-purple-800 border border-purple-200',
        label: 'Administrator'
      };
    } else if (type === 'teacher') {
      return {
        class: 'bg-blue-100 text-blue-800 border border-blue-200',
        label: 'Teacher'
      };
    } else if (type === 'student') {
      return {
        class: 'bg-green-100 text-green-800 border border-green-200',
        label: 'Student'
      };
    }
    return {
      class: 'bg-gray-100 text-gray-800 border border-gray-200',
      label: type
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <User className="mr-2 text-blue-600" />
            User Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : user ? (
          <>
            <div className="flex flex-col sm:flex-row gap-6">
              {/* User Profile Column */}
              <div className="w-full sm:w-1/3 flex flex-col">
                <div className="mb-6 flex flex-col items-center">
                  {user.dp ? (
                    <div className="relative">
                      <img 
                        src={user.dp}
                        alt={`${user.first_name} ${user.last_name}`}
                        className="h-40 w-40 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <span className={`absolute bottom-2 right-2 h-5 w-5 rounded-full border-4 border-white ${user.status === 1 ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="h-40 w-40 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white text-4xl font-medium shadow-lg">
                        {getUserInitials()}
                      </div>
                      <span className={`absolute bottom-2 right-2 h-5 w-5 rounded-full border-4 border-white ${user.status === 1 ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    </div>
                  )}
                  <h3 className="mt-4 text-xl font-bold text-gray-800">{user.first_name} {user.last_name}</h3>
                  <div className="mt-1">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getUserTypeBadge(user.type).class}`}>
                      {getUserTypeBadge(user.type).label}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-lg mb-3 text-gray-700 flex items-center">
                    <Badge className="mr-2 text-blue-600 h-4 w-4" />
                    Quick Info
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-700">
                      <Mail className="mr-2 text-blue-500 h-4 w-4" />
                      <span className="text-sm break-words">{user.email}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Phone className="mr-2 text-blue-500 h-4 w-4" />
                      <span className="text-sm">{user.phone_number}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <CreditCard className="mr-2 text-blue-500 h-4 w-4" />
                      <span className="text-sm">{user.NIC}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Calendar className="mr-2 text-blue-500 h-4 w-4" />
                      <span className="text-sm">Registered: {new Date(user.date).toLocaleDateString('en-US', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs and Details Column */}
              <div className="w-full sm:w-2/3">
                <div className="mb-4 border-b">
                  <ul className="flex flex-wrap -mb-px">
                    <li className="mr-2">
                      <button
                        onClick={() => setActiveTab('personal')}
                        className={`inline-block py-3 px-4 text-sm font-medium border-b-2 ${
                          activeTab === 'personal' 
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } focus:outline-none`}
                      >
                        Personal Information
                      </button>
                    </li>
                    <li className="mr-2">
                      <button
                        onClick={() => setActiveTab('address')}
                        className={`inline-block py-3 px-4 text-sm font-medium border-b-2 ${
                          activeTab === 'address' 
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } focus:outline-none`}
                      >
                        Address Details
                      </button>
                    </li>
                    <li className="mr-2">
                      <button
                        onClick={() => setActiveTab('account')}
                        className={`inline-block py-3 px-4 text-sm font-medium border-b-2 ${
                          activeTab === 'account' 
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } focus:outline-none`}
                      >
                        Account Details
                      </button>
                    </li>
                  </ul>
                </div>

                <div className="p-1">
                  {activeTab === 'personal' && (
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2">Personal Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                          <div className="text-gray-800 font-medium">{user.first_name}</div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                          <div className="text-gray-800 font-medium">{user.last_name}</div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Email Address</label>
                          <div className="text-gray-800 font-medium break-words">{user.email}</div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Phone Number</label>
                          <div className="text-gray-800 font-medium">{user.phone_number}</div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">NIC</label>
                          <div className="text-gray-800 font-medium">{user.NIC}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'address' && (
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2 flex items-center">
                        <MapPin className="mr-2 text-blue-600 h-5 w-5" />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Street Address</label>
                          <div className="text-gray-800 font-medium break-words bg-gray-50 p-3 rounded border border-gray-100">
                            {user.address}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                            <div className="text-gray-800 font-medium">{user.city}</div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">District</label>
                            <div className="text-gray-800 font-medium">{user.district}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'account' && (
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-lg mb-4 text-gray-700 border-b pb-2">Account Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">User ID</label>
                          <div className="text-gray-800 font-medium">{user.user_id}</div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Account Status</label>
                          <div>
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(user.status)}`}>
                              {user.status === 1 ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">User Type</label>
                          <div>
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getUserTypeBadge(user.type).class}`}>
                              {getUserTypeBadge(user.type).label}
                            </span>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Registration Date</label>
                          <div className="text-gray-800 font-medium">
                            {new Date(user.date).toLocaleDateString('en-US', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <User className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">User not found</h3>
            <p className="text-gray-500">The requested user information could not be loaded.</p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          {user && (
            <button
              onClick={() => {
                onClose();
                // Dispatch custom event to be handled by UserManager
                window.dispatchEvent(new CustomEvent('openUpdateModal', { detail: user.user_id }));
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-md text-white font-medium flex items-center transition-colors"
            >
              <Edit2 className="mr-2 h-4 w-4" />
              Update User
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewUser;